import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { closeCurrentPopin, displayPopin } from "../../Hook/popins";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import {
  api_getDailySpinGifts,
  api_getDailySpinSpin,
  dailyspinGifts_gift,
  dailyspinSpin,
} from "../../Api/dailyspin";
import Loader from "../../Component/Loader";
import { USER } from "../../Hook/user";
import useUser from "../../Hook/user";
import "./dailyspin.css";

type Gift = dailyspinGifts_gift & {
  label: string;
  color: string;
};

type SpinResult = {
  gift: Gift;
  spin: dailyspinSpin;
};

const DEFAULT_COLORS = [
  "#f97316",
  "#ec4899",
  "#a855f7",
  "#38bdf8",
  "#f87171",
  "#34d399",
  "#facc15",
  "#22c55e",
];

function DailySpin() {
  const user = useUser();
  const display = displayPopin();
  const [spinning, setSpinning] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [result, setResult] = useState<SpinResult | null>(null);
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const wheelRef = useRef<HTMLDivElement>(null);
  const currentRotation = useRef(0);
  const canPlayDailySpin = user?.informations?.can_play_dailyspin !== false;

  const segmentAngle = gifts.length ? 360 / gifts.length : 0;

  useEffect(() => {
    let isMounted = true;
    api_getDailySpinGifts()
      .then((response) => {
        if (!isMounted) return;
        if (response.error_message || !response.gifts?.length) {
          setGifts([]);
          return;
        }
        setGifts(
          response.gifts.map((gift, index) => ({
            ...gift,
            label: gift.id,
            color: gift.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length],
          })),
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setGifts([]);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const getWinnerId = (spinResponse: dailyspinSpin) => {
    if (typeof spinResponse.gift === "string") return spinResponse.gift;
    if (spinResponse.gift?.id) return spinResponse.gift.id;
    if (spinResponse.id) return spinResponse.id;
    return null;
  };

  const spin = useCallback(() => {
    if (spinning || gifts.length === 0 || !canPlayDailySpin) return;

    setResult(null);
    setError(null);
    setCountdown(3);
    setSpinning(true);
    const spinPromise = api_getDailySpinSpin().catch(
      () => ({ error_message: "dailyspin_spin_failed" }) as any,
    );

    let count = 3;
    const interval = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdown(count);
      } else {
        clearInterval(interval);
        setCountdown(null);
        spinPromise.then((spinResponse: any) => {
          if (spinResponse?.error_message) {
            setError(spinResponse.error_message);
            setSpinning(false);
            return;
          }

          const winnerId = getWinnerId(spinResponse);
          if (!winnerId) {
            setError("dailyspin_spin_missing_gift");
            setSpinning(false);
            return;
          }

          const winnerIndex = gifts.findIndex((gift) => gift.id === winnerId);
          if (winnerIndex === -1) {
            setError("dailyspin_spin_unknown_gift");
            setSpinning(false);
            return;
          }

          const winner = gifts[winnerIndex];

          // The pointer is at top (0deg). Conic gradient segment i spans [i*angle, (i+1)*angle].
          // When wheel is rotated by R degrees, the point originally at Rdeg moves to the top.
          // So we need R % 360 === segmentMid to land that segment under the pointer.
          const extraSpins = 5 * 360;
          let delta = 360 - winnerIndex * segmentAngle - segmentAngle / 2;
          if (delta < 0) delta += 360;
          const targetRotation = currentRotation.current + extraSpins + delta;

          currentRotation.current = targetRotation;

          if (wheelRef.current) {
            wheelRef.current.style.transition =
              "transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)";
            wheelRef.current.style.transform = `rotate(${targetRotation}deg)`;
          }

          setTimeout(() => {
            setSpinning(false);
            setResult({ gift: winner, spin: spinResponse });
            if (USER.informations) {
              USER.informations.can_play_dailyspin = false; 
            }
          }, 4200);
        });
      }
    }, 1000);
  }, [canPlayDailySpin, gifts, segmentAngle, spinning]);

  const handleClaimPrize = () => {
    if (result?.spin.offer_code?.code) {
      display("subscribe", { code: result.spin.offer_code.code });
    }
    closeCurrentPopin();
  };

  return (
    <>
      <div
        onClick={closeCurrentPopin}
        className="top-0 left-0 right-0 overflow-hidden bottom-0 fixed bg-black opacity-60 z-40"
      />
      <div className="z-50 p-6 lg:max-w-md w-screen fixed max-h-[100dvh] overflow-y-auto top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 lg:rounded-xl flex flex-col items-center gap-4 bg-gradient-to-b from-[#d946ef] to-[#fb923c]">
        <div
          onClick={closeCurrentPopin}
          className="absolute top-4 right-4 cursor-pointer text-white"
        >
          <FontAwesomeIcon icon={faClose} className="w-5 h-5" />
        </div>

        <h2 className="text-white text-xl font-bold text-center mt-2">
          <FormattedMessage id="dailyspin__title" />
        </h2>
        <p className="text-white text-sm text-center opacity-90">
          <FormattedMessage id="dailyspin__subtitle" />
        </p>

        {/* Wheel container */}
        <div className="relative overflow-hidden w-[280px] h-[280px]">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/30 rounded-full">
              <Loader />
            </div>
          )}
          {/* Pointer */}
          <div
            className="absolute overflow-hidden -top-3 left-1/2 -translate-x-1/2 z-10 w-0 h-0 drop-shadow-md"
            style={{
              borderLeft: "14px solid transparent",
              borderRight: "14px solid transparent",
              borderTop: "28px solid white",
            }}
          />

          {/* Wheel */}
          <div
            ref={wheelRef}
            className="relative w-full h-full rounded-full overflow-hidden border-[6px] border-white shadow-lg"
          >
            {/* Colored segments via conic gradient */}
            {gifts.length > 0 && (
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: `conic-gradient(${gifts
                    .map(
                      (gift, index) =>
                        `${gift.color} ${index * segmentAngle}deg ${(index + 1) * segmentAngle}deg`,
                    )
                    .join(", ")})`,
                }}
              />
            )}

            {/* Labels layer */}
            {gifts.map((gift, index) => {
              const angle = index * segmentAngle - segmentAngle * 1.25;
              return (
                <div
                  key={gift.id}
                  className="absolute top-1/2 left-1/2 w-0 h-0"
                  style={{
                    transform: `rotate(${angle}deg)`,
                  }}
                >
                  <span
                    className="absolute text-[10px] font-extrabold text-white uppercase whitespace-nowrap origin-left"
                    style={{
                      left: "20px",
                      top: "-8px",
                      textShadow: "1px 1px 2px rgba(0,0,0,0.4)",
                    }}
                  >
                    <FormattedMessage id={gift.label} />
                  </span>
                </div>
              );
            })}

            {/* Center circle */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border-4 border-gray-200 z-[5]" />
          </div>

          {/* Countdown overlay */}
          {countdown !== null && (
            <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
              <span
                className="text-[80px] font-black text-white dailyspin-pop"
                style={{ textShadow: "0 0 30px rgba(0,0,0,0.5)" }}
              >
                {countdown}
              </span>
            </div>
          )}
        </div>

        {/* Result display */}
        {result && (
          <div className="bg-white rounded-lg p-3 w-full text-center dailyspin-reward">
            <p className="text-black font-bold text-lg">
              <FormattedMessage id={result.gift.label} />
            </p>
            {typeof result.spin.days === "number" && (
              <p className="text-sm text-black/70 mt-1">
                +{result.spin.days} days
              </p>
            )}
            {typeof result.spin.tokens === "number" && (
              <p className="text-sm text-black/70 mt-1">
                +{result.spin.tokens} tokens
              </p>
            )}
            {result.spin.offer_code?.code && (
              <p className="text-sm text-black/70 mt-1">
                Code: {result.spin.offer_code.code}
              </p>
            )}
            <button
              onClick={handleClaimPrize}
              className="mt-2 bg-[#d946ef] text-white font-bold py-2 px-6 rounded-full"
            >
              <FormattedMessage id="dailyspin__claim" />
            </button>
          </div>
        )}

        {!result && !canPlayDailySpin && (
          <div className="bg-white rounded-lg p-3 w-full text-center text-black font-semibold">
            <FormattedMessage id="dailyspin__already_played" />
          </div>
        )}

        {!result && error && (
          <div className="bg-white rounded-lg p-3 w-full text-center text-black font-semibold">
            <FormattedMessage id={error} />
          </div>
        )}

        {/* Spin button */}
        {!result && (
          <button
            onClick={spin}
            disabled={
              !canPlayDailySpin ||
              spinning ||
              countdown !== null ||
              isLoading ||
              gifts.length === 0
            }
            className={`bg-white text-[#d946ef] font-extrabold text-lg uppercase py-3.5 px-12 rounded-full tracking-wide ${
              !canPlayDailySpin ||
              spinning ||
              countdown !== null ||
              isLoading ||
              gifts.length === 0
                ? "opacity-50 cursor-not-allowed"
                : "dailyspin-pulse"
            }`}
          >
            <FormattedMessage id="dailyspin__spin" />
          </button>
        )}
      </div>
    </>
  );
}

export default DailySpin;
